import type { DeviceController } from "./device-interface"

interface IntifaceServerDevice {
  DeviceIndex: number
  DeviceName: string
  DeviceMessages: Record<string, any>
}

export class IntifaceDevice implements DeviceController {
  type = "other" as const
  private ws: WebSocket | null = null
  private device: IntifaceServerDevice | null = null
  private isConnected = false
  private connectionMonitorInterval: NodeJS.Timeout | null = null
  private messageId = 1
  private pendingMessages = new Map<number, { resolve: (value: any) => void; reject: (error: any) => void }>()
  private serverUrl = "ws://localhost:12345"
  // See buttplug-device.ts's identical field -- same previously-silent
  // failure, same fix, kept consistent across both Intiface-backed adapters.
  private lastError: string | null = null

  getLastConnectionError(): string | null {
    return this.lastError
  }

  async connect(): Promise<boolean> {
    this.lastError = null
    return new Promise((resolve) => {
      try {
        this.ws = new WebSocket(this.serverUrl)

        this.ws.onopen = () => {
          console.log("[v0] Intiface WebSocket connected")
          this.requestServerInfo()
            .then(() => this.startScanning())
            .then(() => {
              // Wait for device to be added
              setTimeout(() => {
                if (this.device) {
                  this.isConnected = true
                  resolve(true)
                } else {
                  this.lastError = "Connected to Intiface Central, but it reported zero devices within 8 seconds. This means the device itself isn't connected inside Intiface Central yet -- open Intiface Central, confirm your device shows as connected THERE first (not just plugged in/powered on), then click Connect here again."
                  resolve(false)
                }
              }, 8000)
            })
        }

        this.ws.onmessage = (event) => {
          this.handleMessage(JSON.parse(event.data))
        }

        this.ws.onerror = () => {
          const isHttpsPage = typeof window !== "undefined" && window.location.protocol === "https:"
          const isInsecureWs = this.serverUrl.startsWith("ws://")
          this.lastError = isHttpsPage && isInsecureWs
            ? `Could not reach Intiface Central at ${this.serverUrl}. Browsers block an insecure ws:// connection from a secure https:// page -- this page needs to be loaded over http:// (e.g. during local dev) for Intiface's default server to work, since Intiface Central does not serve wss://.`
            : `Could not reach Intiface Central at ${this.serverUrl}. Make sure Intiface Central is running on this computer and its server has been started (the "Start Server" toggle inside Intiface Central).`
          console.error("[v0] Intiface WebSocket error:", this.lastError)
          resolve(false)
        }

        this.ws.onclose = () => {
          console.log("[v0] Intiface WebSocket closed")
          this.isConnected = false
        }
      } catch (error) {
        this.lastError = error instanceof Error ? error.message : "Intiface connection failed for an unknown reason."
        console.error("[v0] Intiface connection failed:", error)
        resolve(false)
      }
    })
  }

  private handleMessage(message: any) {
    // Handle DeviceAdded
    if (message.DeviceAdded) {
      if (!this.device) {
        this.device = message.DeviceAdded
        console.log("[v0] Intiface device added:", this.device!.DeviceName)
      }
      return
    }

    // Handle response messages
    const msgId = message.Id
    if (msgId && this.pendingMessages.has(msgId)) {
      const { resolve, reject } = this.pendingMessages.get(msgId)!
      this.pendingMessages.delete(msgId)

      if (message.Ok) {
        resolve(message.Ok)
      } else if (message.Error) {
        reject(new Error(message.Error.ErrorMessage))
      }
    }
  }

  private sendMessage(message: any): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error("WebSocket not connected"))
        return
      }

      const id = this.messageId++
      message.Id = id
      this.pendingMessages.set(id, { resolve, reject })

      this.ws.send(JSON.stringify([message]))

      // Timeout after 5 seconds
      setTimeout(() => {
        if (this.pendingMessages.has(id)) {
          this.pendingMessages.delete(id)
          reject(new Error("Message timeout"))
        }
      }, 5000)
    })
  }

  private async requestServerInfo() {
    return this.sendMessage({ RequestServerInfo: { ClientName: "Goonru" } })
  }

  private async startScanning() {
    return this.sendMessage({ StartScanning: {} })
  }

  private async stopScanning() {
    return this.sendMessage({ StopScanning: {} })
  }

  async setPosition(position: number, duration?: number): Promise<boolean> {
    if (!this.device || !this.isConnected) return false

    try {
      const normalized = position / 100

      // Use LinearCmd for position
      if (this.device.DeviceMessages.LinearCmd) {
        await this.sendMessage({
          LinearCmd: {
            DeviceIndex: this.device.DeviceIndex,
            Vectors: [{ Index: 0, Duration: duration || 200, Position: normalized }],
          },
        })
        return true
      }

      return false
    } catch (error) {
      console.error("[v0] Intiface setPosition failed:", error)
      return false
    }
  }

  async setVibration(intensity: number): Promise<boolean> {
    if (!this.device || !this.isConnected) return false
    try {
      const normalized = intensity / 100
      if (this.device.DeviceMessages.ScalarCmd) {
        await this.sendMessage({
          ScalarCmd: {
            DeviceIndex: this.device.DeviceIndex,
            Scalars: [{ Index: 0, Scalar: normalized, ActuatorType: "Vibrate" }],
          },
        })
        return true
      }
      return false
    } catch (error) {
      console.error("[v0] Intiface setVibration failed:", error)
      return false
    }
  }

  async setRotation(speed: number): Promise<boolean> {
    if (!this.device || !this.isConnected) return false
    try {
      const normalized = Math.abs((speed - 50) / 50)
      const clockwise = speed >= 50
      if (this.device.DeviceMessages.RotateCmd) {
        await this.sendMessage({
          RotateCmd: {
            DeviceIndex: this.device.DeviceIndex,
            Rotations: [{ Index: 0, Speed: normalized, Clockwise: clockwise }],
          },
        })
        return true
      }
      return false
    } catch (error) {
      console.error("[v0] Intiface setRotation failed:", error)
      return false
    }
  }

  async setOscillation(speed: number): Promise<boolean> {
    if (!this.device || !this.isConnected) return false
    try {
      const normalized = speed / 100
      if (this.device.DeviceMessages.ScalarCmd) {
        await this.sendMessage({
          ScalarCmd: {
            DeviceIndex: this.device.DeviceIndex,
            Scalars: [{ Index: 0, Scalar: normalized, ActuatorType: "Oscillate" }],
          },
        })
        return true
      }
      return false
    } catch (error) {
      console.error("[v0] Intiface setOscillation failed:", error)
      return false
    }
  }

  async setConstrict(level: number): Promise<boolean> {
    if (!this.device || !this.isConnected) return false
    try {
      const normalized = level / 100
      if (this.device.DeviceMessages.ScalarCmd) {
        await this.sendMessage({
          ScalarCmd: {
            DeviceIndex: this.device.DeviceIndex,
            Scalars: [{ Index: 0, Scalar: normalized, ActuatorType: "Constrict" }],
          },
        })
        return true
      }
      return false
    } catch (error) {
      console.error("[v0] Intiface setConstrict failed:", error)
      return false
    }
  }

  // OSR2/SR6 multi-axis via LinearCmd feature indices
  async setSurge(position: number, duration = 200): Promise<boolean> {
    if (!this.device || !this.isConnected) return false
    try {
      if (this.device.DeviceMessages.LinearCmd) {
        await this.sendMessage({
          LinearCmd: {
            DeviceIndex: this.device.DeviceIndex,
            Vectors: [{ Index: 1, Duration: duration, Position: position / 100 }],
          },
        })
        return true
      }
      return false
    } catch (error) {
      console.error("[v0] Intiface setSurge failed:", error)
      return false
    }
  }

  async setSway(position: number, duration = 200): Promise<boolean> {
    if (!this.device || !this.isConnected) return false
    try {
      if (this.device.DeviceMessages.LinearCmd) {
        await this.sendMessage({
          LinearCmd: {
            DeviceIndex: this.device.DeviceIndex,
            Vectors: [{ Index: 2, Duration: duration, Position: position / 100 }],
          },
        })
        return true
      }
      return false
    } catch (error) {
      console.error("[v0] Intiface setSway failed:", error)
      return false
    }
  }

  async setRoll(position: number, duration = 200): Promise<boolean> {
    if (!this.device || !this.isConnected) return false
    try {
      if (this.device.DeviceMessages.LinearCmd) {
        await this.sendMessage({
          LinearCmd: {
            DeviceIndex: this.device.DeviceIndex,
            Vectors: [{ Index: 4, Duration: duration, Position: position / 100 }],
          },
        })
        return true
      }
      return false
    } catch (error) {
      console.error("[v0] Intiface setRoll failed:", error)
      return false
    }
  }

  async setPitch(position: number, duration = 200): Promise<boolean> {
    if (!this.device || !this.isConnected) return false
    try {
      if (this.device.DeviceMessages.LinearCmd) {
        await this.sendMessage({
          LinearCmd: {
            DeviceIndex: this.device.DeviceIndex,
            Vectors: [{ Index: 5, Duration: duration, Position: position / 100 }],
          },
        })
        return true
      }
      return false
    } catch (error) {
      console.error("[v0] Intiface setPitch failed:", error)
      return false
    }
  }

  async stop(): Promise<boolean> {
    if (!this.device || !this.isConnected) return false

    try {
      await this.sendMessage({
        StopDeviceCmd: { DeviceIndex: this.device.DeviceIndex },
      })
      return true
    } catch (error) {
      console.error("[v0] Intiface stop failed:", error)
      return false
    }
  }

  setConnectionKey(key: string) {
    this.serverUrl = key
  }

  getConnectionKey(): string | null {
    return null
  }

  async checkConnection(): Promise<boolean> {
    return this.isConnected && this.ws !== null && this.ws.readyState === WebSocket.OPEN
  }

  async getInfo(): Promise<any> {
    if (!this.device) return null
    return {
      name: this.device.DeviceName,
      index: this.device.DeviceIndex,
      messages: this.device.DeviceMessages,
    }
  }

  async getStatus(): Promise<any> {
    return {
      connected: this.isConnected,
      deviceName: this.device?.DeviceName || null,
    }
  }

  async testMovement(): Promise<boolean> {
    const success1 = await this.setPosition(100, 500)
    await new Promise((resolve) => setTimeout(resolve, 500))
    const success2 = await this.setPosition(0, 500)
    return success1 && success2
  }

  startConnectionMonitor(onStatusChange: (connected: boolean) => void, intervalMs = 5000) {
    this.connectionMonitorInterval = setInterval(async () => {
      const connected = await this.checkConnection()
      if (connected !== this.isConnected) {
        this.isConnected = connected
        onStatusChange(connected)
      }
    }, intervalMs)
  }

  stopConnectionMonitor() {
    if (this.connectionMonitorInterval) {
      clearInterval(this.connectionMonitorInterval)
      this.connectionMonitorInterval = null
    }
  }

  getIsConnected(): boolean {
    return this.isConnected
  }

  disconnect() {
    this.stop()
    if (this.ws) {
      this.ws.close()
    }
    this.device = null
    this.isConnected = false
  }
}
